import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/shared";
import { fmtNOK, fmtNum, RISK } from "@/lib/labels";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import { TrendingUp, Users, Wallet, Target, ShieldAlert, FileSignature, ArrowUpRight, RefreshCw, AlertTriangle, CalendarCheck, StickyNote } from "lucide-react";
import { YE_STEPS, defaultYearEndYear } from "@/components/YearEndChecklist";
import DashboardTimer from "@/components/DashboardTimer";
import TeamOverviewCard from "@/components/TeamOverviewCard";

const RISK_COLORS = { lav: "#10b981", normal: "#3b82f6", forhoyet: "#f59e0b", hoy: "#f97316", uakseptabel: "#ef4444" };

function KPI({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-caps">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-muted-foreground"}`} strokeWidth={1.75} />
      </div>
      <p className="font-display text-3xl font-medium mt-3 tracking-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

const chartTip = {
  contentStyle: { background: "#18181b", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" },
};

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [trend, setTrend] = useState([]);
  const [ye, setYe] = useState(null);
  const [scope, setScope] = useState("alle");
  const [overdue, setOverdue] = useState(null);
  const [note, setNote] = useState("");
  const [noteStatus, setNoteStatus] = useState("");
  const noteTimer = useRef(null);
  const yeYear = defaultYearEndYear();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/dashboard/mrr-trend").then((r) => setTrend(r.data)).catch(() => {});
    api.get(`/year-end/overview?year=${yeYear}`).then((r) => setYe(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setNote(r.data.dashboard_note || "")).catch(() => {});
  }, [yeYear]);

  useEffect(() => {
    const q = scope === "mine" ? "?overdue=true&mine=true" : "?overdue=true";
    api.get(`/tasks${q}`).then((r) => setOverdue(r.data.length)).catch(() => setOverdue(0));
  }, [scope]);

  const saveNote = (val) => {
    setNote(val);
    setNoteStatus("Lagrer…");
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(async () => {
      try { await api.put("/auth/me/note", { note: val }); setNoteStatus("Lagret ✓"); }
      catch { setNoteStatus("Ikke lagret"); }
    }, 700);
  };

  if (!d) return <Page><p className="text-muted-foreground">Laster…</p></Page>;

  const riskData = Object.entries(d.risk_distribution).filter(([, v]) => v > 0).map(([k, v]) => ({ name: RISK[k]?.label || k, value: v, key: k }));
  const overdueCount = overdue ?? d.overdue_tasks;

  return (
    <Page>
      <PageHeader title="Dashboard" subtitle="Oversikt over MRR, ARR, pipeline og risiko" testid="dashboard-header">
        <div className="inline-flex rounded-lg border border-border bg-secondary p-0.5" data-testid="dash-scope-toggle">
          {[["mine", "Mine oppgaver"], ["alle", "Alle oppgaver"]].map(([s, label]) => (
            <button key={s} onClick={() => setScope(s)} data-testid={`dash-scope-${s}`}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${scope === s ? "bg-white text-black font-medium" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </PageHeader>

      <DashboardTimer />

      <TeamOverviewCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-5" data-testid="dashboard-note">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-lg flex items-center gap-2 text-amber-200"><StickyNote className="w-5 h-5" /> Mine notater</h3>
            <span className="text-xs text-amber-400/70">{noteStatus}</span>
          </div>
          <textarea value={note} onChange={(e) => saveNote(e.target.value)} data-testid="dashboard-note-input"
            placeholder="Skriv en personlig huskeliste eller notater her… (lagres automatisk)"
            className="w-full min-h-[150px] bg-transparent resize-y text-sm text-foreground/90 placeholder:text-amber-200/30 focus:outline-none leading-relaxed" />
        </div>

        <button onClick={() => nav("/oppgaver?overdue=1")} data-testid="overdue-card"
          className="relative overflow-hidden rounded-xl border border-border p-5 text-left group"
          style={{ background: "radial-gradient(130% 120% at 100% 0%, #1e293b 0%, #0b1020 55%, #070a12 100%)" }}>
          <p className="label-caps text-white/60">Forfalte gjøremål</p>
          <p className={`font-display text-5xl font-medium mt-2 ${overdueCount > 0 ? "text-white" : "text-emerald-300"}`}>{overdueCount}</p>
          <p className="text-xs text-white/50 mt-2 max-w-[190px]">
            {overdueCount > 0
              ? "Forfalte frister (a-melding, MVA, skattemelding, årsregnskap). Klikk for å rydde opp."
              : "Alt à jour – ingen forfalte gjøremål akkurat nå."}
          </p>
          <span className="inline-flex items-center gap-1 text-xs text-emerald-300 mt-3">Vis forfalte <ArrowUpRight className="w-3.5 h-3.5" /></span>
        </button>
      </div>


      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI testid="kpi-mrr" icon={Wallet} label="Total MRR" value={fmtNOK(d.total_mrr)} sub={`Ny denne mnd: ${fmtNOK(d.new_mrr_month)}`} accent="text-emerald-400" />
        <KPI testid="kpi-arr" icon={TrendingUp} label="Total ARR" value={fmtNOK(d.total_arr)} sub={`Snitt/kunde: ${fmtNOK(d.avg_mrr)}`} accent="text-emerald-400" />
        <KPI testid="kpi-active" icon={Users} label="Aktive kunder" value={fmtNum(d.active_customers)} sub={`${d.onboarding_count} under onboarding`} />
        <KPI testid="kpi-pipeline" icon={Target} label="Pipeline (MRR)" value={fmtNOK(d.pipeline_mrr)} sub={`${d.total_leads} leads`} accent="text-blue-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg">MRR / ARR-utvikling</h3>
            <span className="label-caps">Siste 6 mnd</span>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="mrrGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="month" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip {...chartTip} formatter={(v) => fmtNOK(v)} />
              <Area type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={2} fill="url(#mrrGrad)" name="MRR" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-display text-lg mb-4">Kunder per risikonivå</h3>
          {riskData.length === 0 ? (
            <p className="text-muted-foreground text-sm">Ingen data</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={riskData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {riskData.map((e) => <Cell key={e.key} fill={RISK_COLORS[e.key]} />)}
                </Pie>
                <Tooltip {...chartTip} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            {riskData.map((e) => (
              <span key={e.key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: RISK_COLORS[e.key] }} /> {e.name} ({e.value})
              </span>
            ))}
          </div>
        </div>
      </div>

      {ye && ye.totals.customers > 0 && (() => {
        const totalDone = YE_STEPS.reduce((a, s) => a + (ye.totals.by_step[s.key] || 0), 0);
        const totalPossible = YE_STEPS.reduce((a, s) => a + ((ye.totals.available_by_step || {})[s.key] || 0), 0);
        const pct = totalPossible ? Math.round((totalDone / totalPossible) * 100) : 0;
        return (
          <button
            onClick={() => nav("/arsregnskap")}
            data-testid="widget-yearend"
            className="w-full text-left bg-card border border-border rounded-xl p-5 mt-4 hover:border-emerald-500/40 transition-colors group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-emerald-400" strokeWidth={1.75} />
                <h3 className="font-display text-lg">Årsregnskap {ye.year}</h3>
                <span className="label-caps text-muted-foreground ml-1">{ye.totals.fully_complete} av {ye.totals.customers} kunder ferdig</span>
              </div>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                {pct}% fullført <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} data-testid="widget-yearend-bar" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-4">
              {YE_STEPS.map((s) => {
                const n = ye.totals.by_step[s.key] || 0;
                const total = (ye.totals.available_by_step || {})[s.key] || 0;
                const p = total ? Math.round((n / total) * 100) : 0;
                return (
                  <div key={s.key} title={s.desc}>
                    <p className="text-xs text-muted-foreground truncate">{s.label}</p>
                    <p className="text-sm mt-0.5">{n}/{total}</p>
                    <div className="h-1.5 bg-secondary rounded-full mt-1 overflow-hidden">
                      <div className="h-full bg-emerald-500/70" style={{ width: `${p}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </button>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h3 className="font-display text-lg mb-4">MRR per produkt</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.mrr_by_product} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
              <XAxis type="number" stroke="#71717a" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <YAxis type="category" dataKey="name" stroke="#71717a" fontSize={11} width={110} tickLine={false} axisLine={false} />
              <Tooltip {...chartTip} formatter={(v) => fmtNOK(v)} />
              <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-2 gap-4 content-start">
          <button onClick={() => nav("/kyc")} className="bg-card border border-border rounded-xl p-5 text-left hover:border-amber-500/40 transition-colors group" data-testid="widget-kyc">
            <ShieldAlert className="w-5 h-5 text-amber-400 mb-3" />
            <p className="font-display text-3xl font-medium">{d.kyc_pending}</p>
            <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">KYC til behandling <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" /></p>
          </button>
          <button onClick={() => nav("/avtaler")} className="bg-card border border-border rounded-xl p-5 text-left hover:border-violet-500/40 transition-colors group" data-testid="widget-sign">
            <FileSignature className="w-5 h-5 text-violet-400 mb-3" />
            <p className="font-display text-3xl font-medium">{d.awaiting_signature}</p>
            <p className="text-sm text-muted-foreground mt-1">Venter på signering</p>
          </button>
          <button onClick={() => nav("/onboarding")} className="bg-card border border-border rounded-xl p-5 text-left hover:border-blue-500/40 transition-colors" data-testid="widget-onboarding">
            <Users className="w-5 h-5 text-blue-400 mb-3" />
            <p className="font-display text-3xl font-medium">{d.onboarding_count}</p>
            <p className="text-sm text-muted-foreground mt-1">Under onboarding</p>
          </button>
          <button onClick={() => nav("/kyc")} className="bg-card border border-border rounded-xl p-5 text-left hover:border-amber-500/40 transition-colors" data-testid="widget-renewal">
            <RefreshCw className="w-5 h-5 text-amber-400 mb-3" />
            <p className="font-display text-3xl font-medium">{d.kyc_renewal_due ?? 0}</p>
            <p className="text-sm text-muted-foreground mt-1">KYC-kontroll forfaller</p>
          </button>
          <div className="bg-card border border-border rounded-xl p-5">
            <TrendingUp className="w-5 h-5 text-emerald-400 mb-3" />
            <p className="font-display text-3xl font-medium">{fmtNOK(d.coming_mrr)}</p>
            <p className="text-sm text-muted-foreground mt-1">Kommende MRR</p>
          </div>
        </div>
      </div>
    </Page>
  );
}
